<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { parse, isValid, isAfter, isBefore } from 'date-fns';
import { useLocale } from 'shared/composables/useLocale';
import {
  getIntlDateFormatForLocale,
  CALENDAR_TYPES,
} from '../helpers/DatePickerHelper';

const props = defineProps({
  calendarType: {
    type: String,
    default: '',
  },
  dateValue: Date,
  compareDate: Date,
  isDisabled: Boolean,
});

const emit = defineEmits(['update', 'validate', 'error']);

const { START_CALENDAR, END_CALENDAR } = CALENDAR_TYPES;
const { t } = useI18n();
const { resolvedLocale } = useLocale();

// A data do campo se escreve e se lê no idioma da tela, como o resto do seletor, e não no do
// navegador: com o navegador em inglês e a tela em português, o campo mostrava 9/20/2026 e
// pedia MM/DD/YYYY.
const datePattern = computed(() =>
  getIntlDateFormatForLocale(resolvedLocale.value)
);
const dateFormat = computed(() => datePattern.value.toUpperCase());

const localDateValue = computed({
  get: () => props.dateValue?.toLocaleDateString(resolvedLocale.value) || '',
  set: newValue => {
    const parsedDate = parse(newValue, datePattern.value, new Date());
    if (isValid(parsedDate)) {
      emit('update', parsedDate);
    }
  },
});

const validateDate = () => {
  if (!isValid(props.dateValue)) {
    emit(
      'error',
      t('DATE_PICKER.DATE_RANGE_INPUT.INVALID_FORMAT', {
        format: dateFormat.value,
      })
    );
    return;
  }

  const { calendarType, compareDate, dateValue } = props;
  const isStartCalendar = calendarType === START_CALENDAR;
  const isEndCalendar = calendarType === END_CALENDAR;

  if (compareDate && isStartCalendar && isAfter(dateValue, compareDate)) {
    emit('error', t('DATE_PICKER.DATE_RANGE_INPUT.START_AFTER_END'));
  } else if (compareDate && isEndCalendar && isBefore(dateValue, compareDate)) {
    emit('error', t('DATE_PICKER.DATE_RANGE_INPUT.END_BEFORE_START'));
  } else {
    emit('validate', dateValue);
  }
};
</script>

<template>
  <div class="h-[82px] flex flex-col items-start px-5 gap-1.5 pt-4 w-full">
    <span class="text-sm font-medium text-n-slate-12">
      {{
        calendarType === START_CALENDAR
          ? $t('DATE_PICKER.DATE_RANGE_INPUT.START')
          : $t('DATE_PICKER.DATE_RANGE_INPUT.END')
      }}
    </span>
    <!-- O `w-full` vem do _base.scss no dashboard, e o console do super admin não o tem: lá o campo
    ficava na largura padrão do navegador, e o da data final passava da folha a 390 px. -->
    <input
      v-model="localDateValue"
      type="text"
      class="w-full !text-sm !mb-0 disabled:!outline-n-strong"
      :placeholder="dateFormat"
      :disabled="isDisabled"
      @keypress.enter="validateDate"
    />
  </div>
</template>
